import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Route42Logo } from '../components/brand/Route42Logo';
import { useAuth } from './AuthProvider';
import {
  confirmCognitoEmailVerification,
  requestCognitoEmailVerificationCode,
} from './auth-client';
import { activateRoute42Memberships } from './membership-client';

export function VerifyEmailPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function sendCode() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await requestCognitoEmailVerificationCode();
      setSent(true);
      setNotice('A verification code was sent to your Route42 email address.');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Route42 could not send a verification code.');
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await confirmCognitoEmailVerification(code.trim());
      await activateRoute42Memberships();
      await auth.refreshProfile();
      navigate('/start', { replace: true });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Route42 could not verify this email address.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="r42-auth-page">
      <section className="r42-auth-card">
        <div className="r42-auth-identity">
          <div className="r42-auth-brand"><Route42Logo variant="lockup" /></div>
          <span className="r42-eyebrow r42-auth-tagline">Machine Intelligence</span>
        </div>
        <div className="r42-auth-task">
          <h1>Verify your email</h1>
          <p>
            This Route42 identity still needs email verification before organization access can be activated.
          </p>
        </div>

        {!sent ? (
          <>
            {error ? <div className="r42-form-error" role="alert">{error}</div> : null}
            <button className="btn r42-btn-primary w-100" type="button" disabled={busy} onClick={() => void sendCode()}>
              {busy ? 'Sending…' : 'Send verification code'}
            </button>
            <div className="r42-auth-return"><Link to="/sign-in">Return to sign in</Link></div>
          </>
        ) : (
          <form onSubmit={(event) => void submit(event)}>
            {notice ? <div className="r42-inline-state" role="status">{notice}</div> : null}
            <label>
              Verification code
              <input
                autoComplete="one-time-code"
                inputMode="numeric"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                required
              />
            </label>
            {error ? <div className="r42-form-error" role="alert">{error}</div> : null}
            <button className="btn r42-btn-primary w-100" type="submit" disabled={busy || !code.trim()}>
              {busy ? 'Verifying…' : 'Verify email'}
            </button>
            <button className="r42-auth-secondary-action" type="button" disabled={busy} onClick={() => void sendCode()}>
              Resend verification code
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
